import React, { useState, useEffect } from "react"
import {
  Drawer,
  Collapse,
  Input,
  Button,
  Select,
  DatePicker,
  InputNumber,
  message,
  Form,
} from "antd"
import styled from "styled-components"
import {
  UserOutlined,
  HomeOutlined,
  BankOutlined,
  FileTextOutlined,
  DollarOutlined,
  SearchOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons"
import ReferralDashboardContainer from "container/ReferralDashboard"
import {
  formatPhoneNumber,
  phoneDigits,
  defaultReferralContactErrors,
  validateReferralContactRows,
  validateEscrowEmails,
} from "dumbComponents/ReferralsDashboard/contactValidation"

const { Panel } = Collapse
const { TextArea } = Input
const { Option } = Select

// ─── Styled Components ───────────────────────────────────────────────────────

const DrawerBody = styled.div`
  .ant-collapse {
    background: transparent;
    border: none;
  }
  .ant-collapse > .ant-collapse-item {
    border: 1px solid #e8e8f0;
    border-radius: 10px !important;
    margin-bottom: 16px;
    overflow: hidden;
  }
  .ant-collapse > .ant-collapse-item > .ant-collapse-header {
    font-size: 15px;
    font-weight: 600;
    color: #5b78f6;
    padding: 14px 20px;
    align-items: center;
  }
  .ant-collapse-content > .ant-collapse-content-box {
    padding: 0 20px 20px;
  }
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const SectionIndicator = styled.div`
  width: 4px;
  height: 28px;
  background: #5b78f6;
  border-radius: 2px;
  margin-right: 4px;
`

const SectionIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: #5b78f6;
  font-size: 18px;
`

const FieldRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 14px;
`

const FieldGroup = styled.div`
  flex: 1;
  min-width: 0;
`

const FieldLabel = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
  font-weight: 500;
`

const FullWidthField = styled.div`
  margin-bottom: 14px;
`

const InfoText = styled.div`
  font-size: 11px;
  color: #999;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  gap: 4px;
`

const AgentBlock = styled.div`
  padding: 16px;
  background: #fafafe;
  border: 1px solid #ececf4;
  border-radius: 8px;
  margin-bottom: 14px;
  position: relative;
`

const BlockTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
`

const BlockLabel = styled.span`
  font-weight: 600;
  font-size: 13px;
  color: #5b78f6;
`

const EscrowBlock = styled.div`
  padding: 16px;
  background: #fafafe;
  border: 1px solid #ececf4;
  border-radius: 8px;
  margin-bottom: 14px;
  position: relative;
`

const AddButton = styled(Button)`
  color: #5b78f6;
  border-color: #5b78f6;
  font-size: 12px;
  &:hover {
    color: #3d5bd9;
    border-color: #3d5bd9;
  }
`

const MlsToggleWrap = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
`

const MlsToggleBtn = styled(Button)`
  ${props => props.active && `
    background: #5b78f6;
    color: #fff;
    border-color: #5b78f6;
    &:hover { background: #3d5bd9; border-color: #3d5bd9; color: #fff; }
  `}
`

const FooterWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px 0 8px;
`

const SaveButton = styled(Button)`
  width: 280px;
  height: 44px;
  border-radius: 24px;
  font-size: 15px;
  font-weight: 600;
`

const DrawerTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  font-weight: 600;
  color: #303030;
`

const OrangeCircle = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: #f5a623;
  border-radius: 50%;
  color: #fff;
  font-size: 16px;
`

// ─── Initial State ────────────────────────────────────────────────────────────

const EMPTY_REFERRING = {
  brokerageFirmName: "",
  brokerLicenseNumber: "",
  agentFirstName: "",
  agentLastName: "",
  agentLicenseNumber: "",
  address: "",
  email: "",
  phone: "",
}

const EMPTY_REPRESENTING = {
  search: "",
  brokerageFirmName: "",
  brokerLicenseNumber: "",
  agentName: "",
  agentLicenseNumber: "",
  address: "",
  email: "",
  phone: "",
}

const EMPTY_CLIENT = {
  search: "",
  name: "",
  address: "",
  email: "",
  phone: "",
}

const EMPTY_ESCROW = {
  escrowCompany: "",
  escrowOfficer: "",
  escrowEmail: "",
}

const EMPTY_PROPERTY = {
  mlsMode: "mls_id",
  mlsId: "",
  state: "",
  streetAddress: "",
  aptSuite: "",
  city: "",
  zipCode: "",
  county: "",
}

const EMPTY_DEAL = {
  closingDate: null,
  purchasePrice: "",
  commissionType: "",
  referralFee: "",
  miscNotes: "",
}

const STATE_LIST = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
  "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho",
  "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana",
  "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
  "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada",
  "New Hampshire", "New Jersey", "New Mexico", "New York",
  "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon",
  "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
  "West Virginia", "Wisconsin", "Wyoming",
]

const COMMISSION_TYPES = [
  { label: "Percentage", value: "percentage" },
  { label: "Fixed Fee", value: "fixed_fee" },
]

const initialReferralDrawerContactErrors = () => ({
  ...defaultReferralContactErrors(),
  escrow: [{ email: "" }],
})

// ─── Component ────────────────────────────────────────────────────────────────

const AddReferralContractDrawer = (props) => {
  const {
    isOpen,
    onClose,
    createReferral,
    createReferralResponse,
  } = props

  const { isFetching: isSaving } = createReferralResponse || {}

  const [referring, setReferring] = useState({ ...EMPTY_REFERRING })
  const [representingAgents, setRepresentingAgents] = useState([{ ...EMPTY_REPRESENTING }])
  const [clients, setClients] = useState([{ ...EMPTY_CLIENT }])
  const [property, setProperty] = useState({ ...EMPTY_PROPERTY })
  const [escrows, setEscrows] = useState([{ ...EMPTY_ESCROW }])
  const [deal, setDeal] = useState({ ...EMPTY_DEAL })
  const [contactErrors, setContactErrors] = useState(initialReferralDrawerContactErrors())

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setReferring({ ...EMPTY_REFERRING })
      setRepresentingAgents([{ ...EMPTY_REPRESENTING }])
      setClients([{ ...EMPTY_CLIENT }])
      setProperty({ ...EMPTY_PROPERTY })
      setEscrows([{ ...EMPTY_ESCROW }])
      setDeal({ ...EMPTY_DEAL })
      setContactErrors(initialReferralDrawerContactErrors())
    }
  }, [isOpen])

  // Close on successful save
  useEffect(() => {
    if (createReferralResponse && createReferralResponse.data && !isSaving) {
      message.success("Referral contract created successfully")
      onClose()
    }
  }, [createReferralResponse])

  const updateReferring = (field, value) => setReferring(prev => ({ ...prev, [field]: value }))
  const updateProperty = (field, value) => setProperty(prev => ({ ...prev, [field]: value }))
  const updateDeal = (field, value) => setDeal(prev => ({ ...prev, [field]: value }))

  const updateRepAgent = (index, field, value) => {
    setRepresentingAgents(prev => {
      const next = [...prev]
      next[index] = { ...next[index], [field]: value }
      return next
    })
  }
  const addRepAgent = () => {
    setRepresentingAgents(prev => [...prev, { ...EMPTY_REPRESENTING }])
    setContactErrors(prev => ({
      ...prev,
      representing: [...prev.representing, { email: "", phone: "" }],
    }))
  }
  const removeRepAgent = (index) => {
    setRepresentingAgents(prev => prev.filter((_, i) => i !== index))
    setContactErrors(prev => ({
      ...prev,
      representing: prev.representing.filter((_, i) => i !== index),
    }))
  }

  const updateClient = (index, field, value) => {
    setClients(prev => {
      const next = [...prev]
      next[index] = { ...next[index], [field]: value }
      return next
    })
  }
  const addClient = () => {
    setClients(prev => [...prev, { ...EMPTY_CLIENT }])
    setContactErrors(prev => ({
      ...prev,
      clients: [...prev.clients, { email: "", phone: "" }],
    }))
  }
  const removeClient = (index) => {
    setClients(prev => prev.filter((_, i) => i !== index))
    setContactErrors(prev => ({
      ...prev,
      clients: prev.clients.filter((_, i) => i !== index),
    }))
  }

  const updateEscrow = (index, field, value) => {
    setEscrows(prev => {
      const next = [...prev]
      next[index] = { ...next[index], [field]: value }
      return next
    })
  }
  const addEscrow = () => {
    setEscrows(prev => [...prev, { ...EMPTY_ESCROW }])
    setContactErrors(prev => ({
      ...prev,
      escrow: [...prev.escrow, { email: "" }],
    }))
  }
  const removeEscrow = (index) => {
    setEscrows(prev => prev.filter((_, i) => i !== index))
    setContactErrors(prev => ({
      ...prev,
      escrow: prev.escrow.filter((_, i) => i !== index),
    }))
  }

  const handleSave = () => {
    const rowValidation = validateReferralContactRows({
      referring,
      representingAgents,
      clients,
    })
    const escrowEmailMsgs = validateEscrowEmails(escrows)
    const hasEscrowEmailErr = escrowEmailMsgs.some(Boolean)
    if (rowValidation.hasError || hasEscrowEmailErr) {
      setContactErrors({
        ...rowValidation.errors,
        escrow: escrowEmailMsgs.map(email => ({ email })),
      })
      message.error("Please fix the email and phone errors before saving.")
      return
    }

    const payload = {
      status: "pending",
      transaction_name: property.streetAddress || "",
      referring_agent_info: [
        {
          is_primary: true,
          name: [referring.agentFirstName, referring.agentLastName].filter(Boolean).join(" "),
          email: referring.email,
          phone: phoneDigits(referring.phone),
          license_number: referring.agentLicenseNumber,
          brokerage_name: referring.brokerageFirmName,
          broker_license_number: referring.brokerLicenseNumber,
          address: referring.address,
        },
      ],
      representing_agent_info: representingAgents
        .filter(a => a.agentName || a.email || a.brokerageFirmName)
        .map(a => ({
          name: a.agentName,
          email: a.email,
          phone: phoneDigits(a.phone),
          license_number: a.agentLicenseNumber,
          brokerage_name: a.brokerageFirmName,
          broker_license_number: a.brokerLicenseNumber,
          address: a.address,
        })),
      client_info: {
        clients: clients
          .filter(c => c.name || c.email)
          .map((c, idx) => {
            const nameParts = (c.name || "").split(" ")
            return {
              is_primary: idx === 0,
              client_id: null,
              firstname: nameParts[0] || "",
              lastname: nameParts.slice(1).join(" ") || "",
              email: c.email,
              phone: phoneDigits(c.phone),
              address: c.address,
            }
          }),
      },
      property_info: {
        mls_id: property.mlsMode === "mls_id" ? property.mlsId : "",
        state: property.state,
        street_address: property.streetAddress,
        apt_suite: property.aptSuite,
        city: property.city,
        zip_code: property.zipCode,
        county: property.county,
      },
      escrow_info: escrows
        .filter(e => e.escrowCompany || e.escrowOfficer || e.escrowEmail)
        .map(e => ({
          escrow_company: e.escrowCompany,
          escrow_officer: e.escrowOfficer,
          escrow_email: e.escrowEmail,
        })),
      deal_info: {
        closing_date: deal.closingDate ? deal.closingDate.format("YYYY-MM-DD") : null,
        purchase_price: deal.purchasePrice || null,
        commission_type: deal.commissionType || null,
        referral_fee: deal.referralFee || null,
        miscellaneous_notes: deal.miscNotes || null,
      },
      vendors: {
        vendors: [],
        selected_vendor_id: null,
      },
    }

    createReferral({ payload })
  }

  const panelHeader = (icon, title) => (
    <SectionHeader>
      <SectionIndicator />
      <SectionIcon>{icon}</SectionIcon>
      <span>{title}</span>
    </SectionHeader>
  )

  if (!isOpen) return null

  return (
    <Drawer
      width={700}
      placement="right"
      onClose={onClose}
      visible
      title={(
        <DrawerTitle>
          <OrangeCircle>&#9658;</OrangeCircle>
          Add Referral Contract
        </DrawerTitle>
      )}
      destroyOnClose
    >
      <DrawerBody>
        <Collapse
          defaultActiveKey={["referring", "representing", "client", "property", "escrow", "deal"]}
          expandIconPosition="end"
        >

          {/* ── Referring Broker/Agent Information ── */}
          <Panel
            key="referring"
            header={panelHeader(<UserOutlined />, "Referring Broker/Agent Information")}
          >
            <FieldRow>
              <FieldGroup>
                <FieldLabel>Referring Broker (Brokerage Firm Name)</FieldLabel>
                <Input
                  placeholder="Enter brokerage name"
                  value={referring.brokerageFirmName}
                  onChange={e => updateReferring("brokerageFirmName", e.target.value)}
                />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Referring Broker License #</FieldLabel>
                <Input
                  placeholder="Enter license number"
                  value={referring.brokerLicenseNumber}
                  onChange={e => updateReferring("brokerLicenseNumber", e.target.value)}
                />
              </FieldGroup>
            </FieldRow>

            <FieldRow>
              <FieldGroup>
                <FieldLabel>Referring Agent (First Name)</FieldLabel>
                <Input
                  placeholder="First name"
                  value={referring.agentFirstName}
                  onChange={e => updateReferring("agentFirstName", e.target.value)}
                />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Referring Agent (Last Name)</FieldLabel>
                <Input
                  placeholder="Last name"
                  value={referring.agentLastName}
                  onChange={e => updateReferring("agentLastName", e.target.value)}
                />
              </FieldGroup>
            </FieldRow>

            <FieldRow>
              <FieldGroup>
                <FieldLabel>Referring Agent License #</FieldLabel>
                <Input
                  placeholder="Enter license number"
                  value={referring.agentLicenseNumber}
                  onChange={e => updateReferring("agentLicenseNumber", e.target.value)}
                />
              </FieldGroup>
              <FieldGroup />
            </FieldRow>

            <FullWidthField>
              <FieldLabel>Address Of Referring Brokerage</FieldLabel>
              <Input
                placeholder="Street Address"
                value={referring.address}
                onChange={e => updateReferring("address", e.target.value)}
              />
            </FullWidthField>

            <FieldRow>
              <FieldGroup>
                <FieldLabel>Referring Agent Email Address</FieldLabel>
                <Form.Item
                  style={{ marginBottom: 0 }}
                  validateStatus={contactErrors.referring.email ? "error" : undefined}
                  help={contactErrors.referring.email || undefined}
                >
                  <Input
                    placeholder="Enter email address"
                    value={referring.email}
                    onChange={(e) => {
                      updateReferring("email", e.target.value)
                      if (contactErrors.referring.email) {
                        setContactErrors(prev => ({
                          ...prev,
                          referring: { ...prev.referring, email: "" },
                        }))
                      }
                    }}
                  />
                </Form.Item>
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Referring Agent Phone Number</FieldLabel>
                <Form.Item
                  style={{ marginBottom: 0 }}
                  validateStatus={contactErrors.referring.phone ? "error" : undefined}
                  help={contactErrors.referring.phone || undefined}
                >
                  <Input
                    placeholder="000-000-0000"
                    value={referring.phone}
                    onChange={(e) => {
                      updateReferring("phone", formatPhoneNumber(e.target.value))
                      if (contactErrors.referring.phone) {
                        setContactErrors(prev => ({
                          ...prev,
                          referring: { ...prev.referring, phone: "" },
                        }))
                      }
                    }}
                  />
                </Form.Item>
              </FieldGroup>
            </FieldRow>
          </Panel>

          {/* ── Representing Broker/Agent ── */}
          <Panel
            key="representing"
            header={panelHeader(<UserOutlined />, "Representing Broker/Agent")}
            extra={(
              <AddButton
                size="small"
                icon={<PlusOutlined />}
                onClick={(e) => { e.stopPropagation(); addRepAgent() }}
              >
                Representing Broker/Agent
              </AddButton>
            )}
          >
            {representingAgents.map((agent, idx) => (
              <AgentBlock key={idx}>
                {representingAgents.length > 1 && (
                  <BlockTitle>
                    <BlockLabel>Representing Agent {idx + 1}</BlockLabel>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      size="small"
                      onClick={() => removeRepAgent(idx)}
                    />
                  </BlockTitle>
                )}

                <div style={{ marginBottom: 12 }}>
                  <Input
                    prefix={<SearchOutlined style={{ color: "#bbb" }} />}
                    placeholder="Search by agent name or email address"
                    value={agent.search}
                    onChange={e => updateRepAgent(idx, "search", e.target.value)}
                  />
                </div>
                <InfoText>
                  &#9432; If agent is part of Radius, you can search by agent name. Select an agent from the list.
                </InfoText>

                <FieldRow>
                  <FieldGroup>
                    <FieldLabel>Representing Broker (Brokerage Firm Name)</FieldLabel>
                    <Input
                      placeholder="Enter brokerage name"
                      value={agent.brokerageFirmName}
                      onChange={e => updateRepAgent(idx, "brokerageFirmName", e.target.value)}
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel>Representing Broker License #</FieldLabel>
                    <Input
                      placeholder="Enter license number"
                      value={agent.brokerLicenseNumber}
                      onChange={e => updateRepAgent(idx, "brokerLicenseNumber", e.target.value)}
                    />
                  </FieldGroup>
                </FieldRow>

                <FieldRow>
                  <FieldGroup>
                    <FieldLabel>Representing Agent</FieldLabel>
                    <Input
                      placeholder="Enter agent name"
                      value={agent.agentName}
                      onChange={e => updateRepAgent(idx, "agentName", e.target.value)}
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel>Representing Agent License #</FieldLabel>
                    <Input
                      placeholder="Enter license number"
                      value={agent.agentLicenseNumber}
                      onChange={e => updateRepAgent(idx, "agentLicenseNumber", e.target.value)}
                    />
                  </FieldGroup>
                </FieldRow>

                <FullWidthField>
                  <FieldLabel>Address Of Representing Brokerage</FieldLabel>
                  <Input
                    placeholder="Street Address"
                    value={agent.address}
                    onChange={e => updateRepAgent(idx, "address", e.target.value)}
                  />
                </FullWidthField>

                <FieldRow>
                  <FieldGroup>
                    <FieldLabel>Representing Brokerage Email Address</FieldLabel>
                    <Form.Item
                      style={{ marginBottom: 0 }}
                      validateStatus={contactErrors.representing[idx]?.email ? "error" : undefined}
                      help={contactErrors.representing[idx]?.email || undefined}
                    >
                      <Input
                        placeholder="Enter email address"
                        value={agent.email}
                        onChange={(e) => {
                          updateRepAgent(idx, "email", e.target.value)
                          if (contactErrors.representing[idx] && contactErrors.representing[idx].email) {
                            setContactErrors(prev => {
                              const next = { ...prev, representing: [...prev.representing] }
                              next.representing[idx] = { ...next.representing[idx], email: "" }
                              return next
                            })
                          }
                        }}
                      />
                    </Form.Item>
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel>Representing Brokerage Phone Number</FieldLabel>
                    <Form.Item
                      style={{ marginBottom: 0 }}
                      validateStatus={contactErrors.representing[idx]?.phone ? "error" : undefined}
                      help={contactErrors.representing[idx]?.phone || undefined}
                    >
                      <Input
                        placeholder="000-000-0000"
                        value={agent.phone}
                        onChange={(e) => {
                          updateRepAgent(idx, "phone", formatPhoneNumber(e.target.value))
                          if (contactErrors.representing[idx] && contactErrors.representing[idx].phone) {
                            setContactErrors(prev => {
                              const next = { ...prev, representing: [...prev.representing] }
                              next.representing[idx] = { ...next.representing[idx], phone: "" }
                              return next
                            })
                          }
                        }}
                      />
                    </Form.Item>
                  </FieldGroup>
                </FieldRow>
              </AgentBlock>
            ))}
          </Panel>

          {/* ── Principal / Client Information ── */}
          <Panel
            key="client"
            header={panelHeader(<UserOutlined />, "Principal/Client Information")}
            extra={(
              <AddButton
                size="small"
                icon={<PlusOutlined />}
                onClick={(e) => { e.stopPropagation(); addClient() }}
              >
                Client
              </AddButton>
            )}
          >
            {clients.map((client, idx) => (
              <AgentBlock key={idx}>
                {clients.length > 1 && (
                  <BlockTitle>
                    <BlockLabel>Client {idx + 1}</BlockLabel>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      size="small"
                      onClick={() => removeClient(idx)}
                    />
                  </BlockTitle>
                )}

                <div style={{ marginBottom: 12 }}>
                  <Input
                    prefix={<SearchOutlined style={{ color: "#bbb" }} />}
                    placeholder="Search for client name or email address"
                    value={client.search}
                    onChange={e => updateClient(idx, "search", e.target.value)}
                  />
                </div>
                <InfoText>
                  &#9432; Submit this if you want to link it to the entered address
                </InfoText>

                <FieldRow>
                  <FieldGroup>
                    <FieldLabel>Principal Client Name*</FieldLabel>
                    <Input
                      placeholder="Enter name"
                      value={client.name}
                      onChange={e => updateClient(idx, "name", e.target.value)}
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel>Address</FieldLabel>
                    <Input
                      placeholder="Street Address"
                      value={client.address}
                      onChange={e => updateClient(idx, "address", e.target.value)}
                    />
                  </FieldGroup>
                </FieldRow>

                <FieldRow>
                  <FieldGroup>
                    <FieldLabel>Email</FieldLabel>
                    <Form.Item
                      style={{ marginBottom: 0 }}
                      validateStatus={contactErrors.clients[idx]?.email ? "error" : undefined}
                      help={contactErrors.clients[idx]?.email || undefined}
                    >
                      <Input
                        placeholder="Enter email"
                        value={client.email}
                        onChange={(e) => {
                          updateClient(idx, "email", e.target.value)
                          if (contactErrors.clients[idx] && contactErrors.clients[idx].email) {
                            setContactErrors(prev => {
                              const next = { ...prev, clients: [...prev.clients] }
                              next.clients[idx] = { ...next.clients[idx], email: "" }
                              return next
                            })
                          }
                        }}
                      />
                    </Form.Item>
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel>Phone Number</FieldLabel>
                    <Form.Item
                      style={{ marginBottom: 0 }}
                      validateStatus={contactErrors.clients[idx]?.phone ? "error" : undefined}
                      help={contactErrors.clients[idx]?.phone || undefined}
                    >
                      <Input
                        placeholder="000-000-0000"
                        value={client.phone}
                        onChange={(e) => {
                          updateClient(idx, "phone", formatPhoneNumber(e.target.value))
                          if (contactErrors.clients[idx] && contactErrors.clients[idx].phone) {
                            setContactErrors(prev => {
                              const next = { ...prev, clients: [...prev.clients] }
                              next.clients[idx] = { ...next.clients[idx], phone: "" }
                              return next
                            })
                          }
                        }}
                      />
                    </Form.Item>
                  </FieldGroup>
                </FieldRow>
              </AgentBlock>
            ))}
          </Panel>

          {/* ── Property Information ── */}
          <Panel
            key="property"
            header={panelHeader(<HomeOutlined />, "Property Information")}
          >
            <MlsToggleWrap>
              <MlsToggleBtn
                size="small"
                active={property.mlsMode === "mls_id" ? 1 : 0}
                onClick={() => updateProperty("mlsMode", "mls_id")}
              >
                MLS ID
              </MlsToggleBtn>
              <MlsToggleBtn
                size="small"
                active={property.mlsMode === "property_address" ? 1 : 0}
                onClick={() => updateProperty("mlsMode", "property_address")}
              >
                Property Address
              </MlsToggleBtn>
            </MlsToggleWrap>

            {property.mlsMode === "mls_id" && (
              <FullWidthField>
                <FieldLabel>MLS ID</FieldLabel>
                <Input
                  placeholder="Enter MLS ID"
                  value={property.mlsId}
                  onChange={e => updateProperty("mlsId", e.target.value)}
                />
              </FullWidthField>
            )}

            <FieldRow>
              <FieldGroup>
                <FieldLabel>Type (State)</FieldLabel>
                <Select
                  style={{ width: "100%" }}
                  placeholder="Select State"
                  value={property.state || undefined}
                  onChange={val => updateProperty("state", val)}
                  showSearch
                >
                  {STATE_LIST.map(s => <Option key={s} value={s}>{s}</Option>)}
                </Select>
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Street Address</FieldLabel>
                <Input
                  placeholder="Street Address"
                  value={property.streetAddress}
                  onChange={e => updateProperty("streetAddress", e.target.value)}
                />
              </FieldGroup>
            </FieldRow>

            <FieldRow>
              <FieldGroup>
                <FieldLabel>Apt/Suite</FieldLabel>
                <Input
                  placeholder="Apt/Suite"
                  value={property.aptSuite}
                  onChange={e => updateProperty("aptSuite", e.target.value)}
                />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>City</FieldLabel>
                <Input
                  placeholder="City"
                  value={property.city}
                  onChange={e => updateProperty("city", e.target.value)}
                />
              </FieldGroup>
            </FieldRow>

            <FieldRow>
              <FieldGroup>
                <FieldLabel>Zip Code</FieldLabel>
                <Input
                  placeholder="Zip Code"
                  value={property.zipCode}
                  onChange={e => updateProperty("zipCode", e.target.value)}
                />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>County</FieldLabel>
                <Input
                  placeholder="County"
                  value={property.county}
                  onChange={e => updateProperty("county", e.target.value)}
                />
              </FieldGroup>
            </FieldRow>
          </Panel>

          {/* ── Escrow Information ── */}
          <Panel
            key="escrow"
            header={panelHeader(<BankOutlined />, "Escrow Information")}
            extra={(
              <AddButton
                size="small"
                icon={<PlusOutlined />}
                onClick={(e) => { e.stopPropagation(); addEscrow() }}
              >
                Add Escrow
              </AddButton>
            )}
          >
            {escrows.map((escrow, idx) => (
              <EscrowBlock key={idx}>
                {escrows.length > 1 && (
                  <BlockTitle>
                    <BlockLabel>Escrow Information ({idx + 1})</BlockLabel>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      size="small"
                      onClick={() => removeEscrow(idx)}
                    />
                  </BlockTitle>
                )}
                {escrows.length === 1 && (
                  <BlockTitle>
                    <BlockLabel>Escrow Information (1)</BlockLabel>
                  </BlockTitle>
                )}

                <FieldRow>
                  <FieldGroup>
                    <FieldLabel>Escrow Company*</FieldLabel>
                    <Input
                      placeholder="Enter escrow company"
                      value={escrow.escrowCompany}
                      onChange={e => updateEscrow(idx, "escrowCompany", e.target.value)}
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel>Escrow Officer*</FieldLabel>
                    <Input
                      placeholder="Enter escrow officer name"
                      value={escrow.escrowOfficer}
                      onChange={e => updateEscrow(idx, "escrowOfficer", e.target.value)}
                    />
                  </FieldGroup>
                </FieldRow>

                <FullWidthField>
                  <FieldLabel>Email</FieldLabel>
                  <Form.Item
                    style={{ marginBottom: 0 }}
                    validateStatus={contactErrors.escrow[idx]?.email ? "error" : undefined}
                    help={contactErrors.escrow[idx]?.email || undefined}
                  >
                    <Input
                      placeholder="Enter escrow email"
                      value={escrow.escrowEmail}
                      onChange={(e) => {
                        updateEscrow(idx, "escrowEmail", e.target.value)
                        if (contactErrors.escrow[idx] && contactErrors.escrow[idx].email) {
                          setContactErrors(prev => {
                            const next = { ...prev, escrow: [...prev.escrow] }
                            next.escrow[idx] = { ...next.escrow[idx], email: "" }
                            return next
                          })
                        }
                      }}
                    />
                  </Form.Item>
                </FullWidthField>
              </EscrowBlock>
            ))}
          </Panel>

          {/* ── Deal Details ── */}
          <Panel
            key="deal"
            header={panelHeader(<DollarOutlined />, "Deal Details")}
          >
            <FieldRow>
              <FieldGroup>
                <FieldLabel>Estimated Close of Escrow / Contract Date</FieldLabel>
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="Select date"
                  value={deal.closingDate}
                  onChange={val => updateDeal("closingDate", val)}
                  format="MM/DD/YYYY"
                />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Purchase Price / Total Amount of Deal*</FieldLabel>
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="$ Enter amount"
                  value={deal.purchasePrice}
                  onChange={val => updateDeal("purchasePrice", val)}
                  formatter={val => val ? `$ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""}
                  parser={val => val.replace(/\$\s?|(,*)/g, "")}
                  min={0}
                />
              </FieldGroup>
            </FieldRow>

            <FieldRow>
              <FieldGroup>
                <FieldLabel>Commission Type</FieldLabel>
                <Select
                  style={{ width: "100%" }}
                  placeholder="Select type"
                  value={deal.commissionType || undefined}
                  onChange={val => updateDeal("commissionType", val)}
                >
                  {COMMISSION_TYPES.map(ct => (
                    <Option key={ct.value} value={ct.value}>{ct.label}</Option>
                  ))}
                </Select>
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Referral Fee*</FieldLabel>
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="Enter referral fee"
                  value={deal.referralFee}
                  onChange={val => updateDeal("referralFee", val)}
                  min={0}
                />
              </FieldGroup>
            </FieldRow>

            <FullWidthField>
              <FieldLabel>Miscellaneous Notes</FieldLabel>
              <TextArea
                rows={3}
                placeholder="Enter your notes here..."
                value={deal.miscNotes}
                onChange={e => updateDeal("miscNotes", e.target.value)}
              />
            </FullWidthField>
          </Panel>

        </Collapse>

        <FooterWrap>
          <SaveButton
            type="primary"
            onClick={handleSave}
            loading={isSaving}
          >
            Save &amp; Proceed
          </SaveButton>
        </FooterWrap>
      </DrawerBody>
    </Drawer>
  )
}

export default ReferralDashboardContainer(AddReferralContractDrawer)
